import React, { useState, useCallback } from 'react';
import { Upload, FileText, Brain, TrendingDown, MessageCircle, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Progress } from '../../../components/ui/progress';
import { Badge } from '../../../components/ui/badge';
import { Alert, AlertDescription } from '../../../components/ui/alert';

const ExposeUpload = ({ onDataExtracted }) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [negotiationTips, setNegotiationTips] = useState(null);
  const [progress, setProgress] = useState(0);

  const handleFileUpload = useCallback(async (file) => {
    setUploadedFile(file);
    setIsProcessing(true);
    setProgress(0);

    try {
      // Schritt 1: OCR-Verarbeitung
      setProgress(25);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Schritt 2: KI-Datenextraktion
      setProgress(50);
      const mockExtractedData = {
        address: "Musterstraße 15, 68159 Mannheim",
        price: 320000,
        livingSpace: 85,
        rooms: 3.5,
        yearBuilt: 1985,
        condition: "Renovierungsbedürftig",
        rent: 1200,
        additionalCosts: 180,
        heatingType: "Gasheizung (Baujahr 1995)",
        balcony: false,
        parking: true,
        listingDuration: "4 Monate online"
      };
      
      // Schritt 3: Marktanalyse
      setProgress(75);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Schritt 4: Verhandlungsstrategien generieren
      setProgress(100);
      const mockNegotiationTips = {
        marketAnalysis: {
          averagePrice: 305000,
          pricePerSqm: 3588,
          marketTrend: "stabil",
          competitorCount: 12
        },
        negotiationPoints: [
          {
            category: "Sanierungsbedarf",
            impact: -25000,
            argument: "Heizung (30 Jahre alt) muss erneuert werden",
            priority: "hoch"
          },
          {
            category: "Fehlende Ausstattung", 
            impact: -10000,
            argument: "Keine Balkone/Terrassen vorhanden",
            priority: "mittel"
          },
          {
            category: "Lange Vermarktungszeit",
            impact: -15000,
            argument: "4 Monate online → Verkaufsdruck",
            priority: "hoch"
          }
        ],
        recommendedOffer: {
          initial: 270000,
          justification: "15,6% unter Angebotspreis aufgrund Sanierungsbedarf",
          maxPrice: 295000,
          savingsPotential: 25000
        },
        negotiationScript: {
          opening: "Basierend auf meiner Marktanalyse und dem Sanierungsbedarf würde ich 270.000€ anbieten.",
          arguments: [
            "Vergleichbare Objekte in Mannheim kosten durchschnittlich 305.000€",
            "Die 30 Jahre alte Heizung muss zeitnah erneuert werden (ca. 25.000€)",
            "Fehlende Balkone reduzieren die Attraktivität für Mieter"
          ],
          compromise: "Bei 295.000€ könnte ich eine sofortige Zusage geben."
        }
      };

      setExtractedData(mockExtractedData);
      setNegotiationTips(mockNegotiationTips);
      
      if (onDataExtracted) {
        onDataExtracted({
          ...mockExtractedData,
          negotiationAnalysis: mockNegotiationTips
        });
      }

    } catch (error) {
      console.error('Fehler bei der Verarbeitung:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [onDataExtracted]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files[0]) {
      handleFileUpload(files[0]);
    }
  }, [handleFileUpload]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Exposé-Upload & KI-Analyse
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!uploadedFile ? (
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => document.getElementById('file-input').click()}
            >
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-700 mb-2">
                Exposé hier ablegen oder klicken
              </p>
              <p className="text-sm text-gray-500">
                PDF, JPG oder PNG • Max. 10MB
              </p>
              <input
                id="file-input"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0])}
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
                <span className="font-medium">{uploadedFile.name}</span>
                <Badge variant="secondary">{Math.round(uploadedFile.size / 1024)} KB</Badge>
              </div>
              
              {isProcessing && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>KI-Analyse läuft...</span>
                    <span>{progress}%</span>
                  </div>
                  <Progress value={progress} className="w-full" />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {extractedData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5" />
              Extrahierte Objektdaten
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Kaufpreis</p>
                <p className="font-semibold">{extractedData.price.toLocaleString()}€</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Wohnfläche</p>
                <p className="font-semibold">{extractedData.livingSpace} m²</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Zimmer</p>
                <p className="font-semibold">{extractedData.rooms}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Baujahr</p>
                <p className="font-semibold">{extractedData.yearBuilt}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Kaltmiete</p>
                <p className="font-semibold">{extractedData.rent}€</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Nebenkosten</p>
                <p className="font-semibold">{extractedData.additionalCosts}€</p>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Adresse:</strong> {extractedData.address}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                <strong>Zustand:</strong> {extractedData.condition}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {negotiationTips && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingDown className="h-5 w-5" />
              KI-Verhandlungsstrategien
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h4 className="font-semibold mb-3">Marktanalyse</h4>
              <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Durchschnittspreis</p>
                  <p className="font-semibold">{negotiationTips.marketAnalysis.averagePrice.toLocaleString()}€</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Preis/m²</p>
                  <p className="font-semibold">{negotiationTips.marketAnalysis.pricePerSqm}€</p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-3">Verhandlungsargumente</h4>
              <div className="space-y-3">
                {negotiationTips.negotiationPoints.map((point, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{point.category}</p>
                      <p className="text-sm text-gray-600">{point.argument}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant={point.priority === 'hoch' ? 'destructive' : 'secondary'}>
                        {point.priority}
                      </Badge>
                      <p className="font-semibold text-red-600 mt-1">
                        {point.impact.toLocaleString()}€
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-semibold">
                    Empfohlenes Eröffnungsangebot: {negotiationTips.recommendedOffer.initial.toLocaleString()}€
                  </p>
                  <p className="text-sm">
                    {negotiationTips.recommendedOffer.justification}
                  </p>
                  <p className="text-sm">
                    <strong>Einsparpotenzial:</strong> bis zu {negotiationTips.recommendedOffer.savingsPotential.toLocaleString()}€
                  </p>
                </div>
              </AlertDescription>
            </Alert>

            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <MessageCircle className="h-4 w-4" />
                Verhandlungsscript
              </h4>
              <div className="space-y-3 p-4 bg-green-50 rounded-lg">
                <div>
                  <p className="font-medium text-green-800">Eröffnung:</p>
                  <p className="text-sm italic">"{negotiationTips.negotiationScript.opening}"</p>
                </div>
                <div>
                  <p className="font-medium text-green-800">Argumente:</p>
                  <ul className="text-sm space-y-1 ml-4">
                    {negotiationTips.negotiationScript.arguments.map((arg, index) => (
                      <li key={index} className="list-disc">"{arg}"</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-green-800">Kompromiss:</p>
                  <p className="text-sm italic">"{negotiationTips.negotiationScript.compromise}"</p>
                </div>
              </div>
            </div>

            <Button 
              className="w-full" 
              onClick={() => onDataExtracted && onDataExtracted(extractedData)}
            >
              Daten in Objektanalyse übernehmen
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ExposeUpload;
